import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, Zap, Clock, TrendingUp } from 'lucide-react';

export const PerformanceMonitor = ({ show = false }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [performanceMetrics, setPerformanceMetrics] = useState({});
  const [communityLoadTime, setCommunityLoadTime] = useState(null);

  useEffect(() => {
    // Monitor online/offline status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Enhanced performance monitoring
    const observePerformance = () => {
      // Monitor Core Web Vitals for Caribbean Community Platform
      if ('PerformanceObserver' in window) {
        const lcpObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          const lastEntry = entries[entries.length - 1];
          setPerformanceMetrics(prev => ({ ...prev, lcp: lastEntry.startTime }));
          console.log('DP Performance LCP:', lastEntry.startTime);
        });
        
        try {
          lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        } catch (e) {
          // Browser doesn't support this observer
          console.log('LCP observer not supported');
        }

        // First Input Delay (FID)
        const fidObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          const lastEntry = entries[entries.length - 1];
          setPerformanceMetrics(prev => ({ ...prev, fid: lastEntry.processingStart - lastEntry.startTime }));
          console.log('DP Performance FID:', lastEntry.processingStart - lastEntry.startTime);
        });

        try {
          fidObserver.observe({ entryTypes: ['first-input'] });
        } catch (e) {
          console.log('FID observer not supported');
        }
      }
    };

    observePerformance();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!show) return null;

  const getPerformanceColor = (time) => {
    if (time < 1000) return 'text-green-600';
    if (time < 3000) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceMessage = (time) => {
    if (time < 1000) return 'Excellent';
    if (time < 3000) return 'Good';
    return 'Needs optimization';
  };

  return (
    <div className="fixed bottom-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-sm z-50">
      <div className="text-sm space-y-2">
        <div className="flex items-center justify-between">
          <span className="font-medium">🏝️ Performance Monitor</span>
          <div className="flex items-center space-x-2">
            {isOnline ? (
              <Wifi className="w-4 h-4 text-green-600" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-600" />
            )}
            <span className={isOnline ? 'text-green-600' : 'text-red-600'}>
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>
        
        {performanceMetrics.lcp && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <Clock className="w-4 h-4 text-gray-500" />
              <span>LCP:</span>
            </div>
            <div className={`font-medium ${getPerformanceColor(performanceMetrics.lcp)}`}>
              {(performanceMetrics.lcp / 1000).toFixed(2)}s
            </div>
          </div>
        )}

        {performanceMetrics.fid && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <TrendingUp className="w-4 h-4 text-gray-500" />
              <span>FID:</span>
            </div>
            <div className={`font-medium ${getPerformanceColor(performanceMetrics.fid)}`}>
              {performanceMetrics.fid.toFixed(2)}ms
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            <Zap className="w-4 h-4 text-gray-500" />
            <span>PWA:</span>
          </div>
          <span className="text-green-600 font-medium">Active</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            <span>🍽️</span>
            <span>Experience:</span>
          </div>
          <span className="text-orange-600 font-medium">Optimized</span>
        </div>

        {!isOnline && (
          <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
            🏝️ Offline mode: Caribbean content cached!
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceMonitor;